<template>
  <div class="flex items-center justify-center h-screen p-4 text-white login-image">
    <Toast />
    <div class="w-full sm:w-72 sm:h-96 animated fadeIn">
      <div class="p-8 bg-dark-widget rounded-lg shadow">
        <img class="p-4" src="../assets/images/logo.svg" />
        <p class="text-center text-white">WISION IoT Platform</p>
        <form class="my-4 space-y-4">
          <fieldset>
            <div class="relative text-gray-300">
              <input
                type="text"
                ref="username"
                required
                v-model="username"
                :placeholder="$t('login.username')"
                class="w-full h-10 pl-10 pr-3 text-base placeholder-gray-300 bg-gray-700 rounded-lg focus:shadow-outline"
              />
              <div class="absolute inset-y-0 left-0 flex items-center px-2 pointer-events-none">
                <svg class="fill-current" xmlns="http://www.w3.org/2000/svg" height="24" width="24">
                  <path d="M0 0h24v24H0V0z" fill="none"/>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM7.07 18.28c.43-.9 3.05-1.78 4.93-1.78s4.51.88 4.93 1.78C15.57 19.36 13.86 20 12 20s-3.57-.64-4.93-1.72zm11.29-1.45c-1.43-1.74-4.9-2.33-6.36-2.33s-4.93.59-6.36 2.33A7.95 7.95 0 014 12c0-4.41 3.59-8 8-8s8 3.59 8 8c0 1.82-.62 3.49-1.64 4.83zM12 6c-1.94 0-3.5 1.56-3.5 3.5S10.06 13 12 13s3.5-1.56 3.5-3.5S13.94 6 12 6zm0 5c-.83 0-1.5-.67-1.5-1.5S11.17 8 12 8s1.5.67 1.5 1.5S12.83 11 12 11z"/>
                </svg>
              </div>
            </div>
          </fieldset>
          <fieldset>
            <div class="relative text-gray-300">
              <input
                type="password"
                v-model="password"
                v-on:keyup.enter="onClickLogin"
                required
                :placeholder="$t('login.password')"
                class="w-full h-10 pl-10 pr-3 text-base placeholder-gray-300 bg-gray-700 rounded-lg focus:shadow-outline"
              />
              <div class="absolute inset-y-0 left-0 flex items-center px-2 pointer-events-none">
                <svg class="fill-current" xmlns="http://www.w3.org/2000/svg" height="24" width="24">
                  <g fill="none">
                    <path d="M0 0h24v24H0V0z"/>
                    <path d="M0 0h24v24H0V0z" opacity=".87"/>
                  </g>
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                </svg>
              </div>
            </div>
          </fieldset>
          <button class="flex items-center justify-around w-full py-2 bg-blue-500 border border-blue-700 rounded active:bg-blue-400 disabled:opacity-70" type="button" :disabled="loading" @click="onClickLogin">
            <svg v-if="loading" class="w-6 h-6 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>

            <span>{{ $t('login.signin') }}</span>

            <div v-if="loading" class="w-6 h-6"></div>
          </button>

        </form>

        <div class="my-4 text-center">
          <a class="text-white" :href=contactUrl target="_blank" rel="noopener">
            {{ $t('login.contactus') }}
          </a>
        </div>

        <div class="text-xs text-center text-gray-500">
          {{ releaseInfo() }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.login-image {
  background: url(../assets/images/login-sm.jpg) 50%/cover no-repeat fixed;
  @screen md {
    background-image: url(../assets/images/login-md.jpg);
  }
  @screen lg {
    background-image: url(../assets/images/login-lg.jpg);
  }
  @screen xl {
    background-image: url(../assets/images/login-xl.jpg);
  }
}
</style>

<script lang="ts">
import Vue from 'vue';
import { mapActions } from 'vuex';
import { contactUrl } from '@/../../../../config';
import { ActionTypes as UserActions } from '@/core/store/modules/user';
import Toast from '@/components/Toast.vue';
interface Data {
  contactUrl: string;
  password: string;
  username: string;
  loading: boolean;
}

export default Vue.extend({
  components: {
    Toast,
  },
  data(): Data {
    return {
      contactUrl,
      loading: false,
      password: '',
      username: '',
    };
  },
  methods: {
    ...mapActions({
      login: `user/${UserActions.USER_LOGIN}`,
    }),

    async onClickLogin(): Promise<void> {
      const targetRoute = this.$route.query.redirect ? { path: this.$route.query.redirect } : { name: 'root' };
      try {
        this.loading = true;

        await this.login({
          username: this.username,
          password: this.password,
        });

        this
          .$nextTick()
          .then(() => this.$router.push(targetRoute));
      } catch (err) {
        this.loading = false;
      }
    },
    releaseInfo(): string {
      return `v${process.env.VUE_APP_WISION_RELEASE_ID}`;
    },
  },
  mounted(): void {
    this.$refs.username.focus();
  },
});
</script>
